import React, { useEffect, useState } from 'react';
import { Box, TextField, InputAdornment } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import BankStatusCard from './BankStatusCard';
import BankDocumentPreview from './BankDocumentPreview';
import { getBankFarmerColumns } from '../components/GridColumns';
import { useBankFarmers } from '../components/api/queries/hooks';

export default function BankDocumentVerification() {
    const pageNumber = 1;
    const pageSize = 50;

    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedPartnerId, setSelectedPartnerId] = useState(null);

    const [expandedRowId, setExpandedRowId] = useState(null);
    const [filterStatus, setFilterStatus] = useState('ALL');
    const [searchText, setSearchText] = useState('');
    const [filterText, setFilterText] = useState('');
    const { data, isLoading, error } = useBankFarmers({ pageNumber, pageSize });

    const mapStatus = (status) => {
        switch (status) {
            case 'BANK_DOCUMENT_VERIFIED':
                return 'VERIFIED';
            case 'BANK_DOCUMENT_REJECTED':
                return 'REJECTED';
            case 'BANK_DOCUMENT_RECEIVED':
                return 'RECEIVED';
            case 'BANK_DOCUMENT_ON_HOLD':
                return 'ON_HOLD'; // mapped correctly
            default:
                return status;
        }
    };

    useEffect(() => {
        if (data) {
            const farmers = data?.farmers || [];
            const formattedRows = farmers.map((item) => ({
                ...item,
                id: item.farmerId,
                mappedStatus: mapStatus(item.bankStatus),
            }));
            setDocuments(formattedRows);
        }
    }, [data, isLoading]);

    const handleRowAction = (row) => {
        setExpandedRowId(expandedRowId === row.id ? null : row.id);
    };

    // ----------------- COUNTS --------------------
    const total = documents.length;
    const approved = documents.filter(d => d.mappedStatus === 'VERIFIED').length;
    const rejected = documents.filter(d => d.mappedStatus === 'REJECTED').length;
    const pending = documents.filter(d => d.mappedStatus === 'RECEIVED').length;
    const onHold = documents.filter(d => d.mappedStatus === 'ON_HOLD').length;

    // Filtered rows
    const filteredDocuments = documents
        .filter(
            (doc) =>
                doc.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                doc.phoneNumber?.includes(searchText)
        )
        .filter(
            (doc) =>
                doc.partnerName?.toLowerCase().includes(filterText.toLowerCase()) ||
                String(doc.partnerId)?.includes(filterText)
        )
        .filter(
            (doc) =>
                filterStatus === 'ALL' || doc.mappedStatus === filterStatus
        );
    return (
        <Box sx={{ padding: '20px' }}>
            <h1 style={{ marginBottom: '20px' }}>Bank Document Verification</h1>
            {/* STATUS CARDS */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'space-between',
                    alignItems: 'stretch',
                    marginBottom: '20px',
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                }}
            >
                <Box sx={{ flex: 1 }}>
                    <BankStatusCard
                        title="Total Farmers"
                        count={total}
                        description="With bank details updated"
                        status="TOTAL"
                        onClick={() => setFilterStatus('ALL')}
                    />
                </Box>

                <Box sx={{ flex: 1 }}>
                    <BankStatusCard
                        title="Approved"
                        count={approved}
                        description="Documents verified"
                        status="VERIFIED"
                        onClick={() => setFilterStatus('VERIFIED')}
                    />
                </Box>

                <Box sx={{ flex: 1 }}>
                    <BankStatusCard
                        title="Rejected"
                        count={rejected}
                        description="Documents rejected"
                        status="REJECTED"
                        onClick={() => setFilterStatus('REJECTED')}
                    />
                </Box>

                <Box sx={{ flex: 1 }}>
                    <BankStatusCard
                        title="Pending"
                        count={pending}
                        description="Awaiting review"
                        status="RECEIVED"
                        onClick={() => setFilterStatus('RECEIVED')}
                    />
                </Box>

                <Box sx={{ flex: 1 }}>
                    <BankStatusCard
                        title="On Hold"
                        count={onHold}
                        description="Documents placed on hold"
                        status="ON_HOLD"
                        onClick={() => setFilterStatus('ON_HOLD')}
                    />
                </Box>
            </Box>

            {/* SEARCH BAR */}
            <Box sx={{ mb: 2 }}>
                <TextField
                    fullWidth
                    placeholder="Search by Farmer Name "
                    variant="outlined"
                    size="medium"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    sx={{
                        backgroundColor: '#fff',
                        borderRadius: 2,
                        boxShadow: '0px 2px 6px rgba(0,0,0,0.08)',
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: 'transparent' },
                            '&:hover fieldset': { borderColor: 'transparent' },
                            '&.Mui-focused fieldset': { borderColor: 'transparent' }
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                        sx: { '& input::placeholder': { color: '#7B8794', opacity: 1 } }
                    }}
                />
            </Box>

            {/* FILTER BAR */}
            <Box sx={{ mb: 2 }}>
                <TextField
                    fullWidth
                    placeholder="Filter by Partner ID"
                    variant="outlined"
                    size="medium"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    sx={{
                        backgroundColor: '#fff',
                        borderRadius: 2,
                        boxShadow: '0px 2px 6px rgba(0,0,0,0.08)',
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: 'transparent' },
                            '&:hover fieldset': { borderColor: 'transparent' },
                            '&.Mui-focused fieldset': { borderColor: 'transparent' }
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <PersonIcon color="action" />
                            </InputAdornment>
                        ),
                        sx: { '& input::placeholder': { color: '#7B8794', opacity: 1 } }
                    }}
                />
            </Box>

            {/* GRID TABLE */}
            <DataGridPro
                loading={loading}
                rows={filteredDocuments}
                columns={getBankFarmerColumns(handleRowAction, expandedRowId)}
                pageSize={20}
                getRowId={(row) => row.id}
                disableSelectionOnClick
                autoHeight
                columnVisibilityModel={{
                    __detail_panel_toggle__: false,
                }}
                getDetailPanelContent={({ row }) => (
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: '#f9f9f9',
                            borderRadius: 1,
                        }}
                    >
                        <BankDocumentPreview document={row} />
                    </Box>
                )}
                getDetailPanelHeight={() => 560}
                detailPanelExpandedRowIds={
                    expandedRowId ? [expandedRowId] : []
                }
                sx={{
                    '& .MuiDataGrid-root': {
                        backgroundColor: '#fafafa',
                        borderRadius: 2,
                    },
                    '& .MuiDataGrid-columnHeader': {
                        backgroundColor: '#f0f0f0',
                    },
                }}
                components={{
                    NoRowsOverlay: () => (
                        <Box
                            sx={{
                                display: 'flex',
                                height: '100%',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#7B8794',
                            }}
                        >
                            No Records Found
                        </Box>
                    ),
                }}
            />
        </Box>
    );
}
